import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isVoiceDrivenSceneUsingPlaceholderBaseAsset } from 'js/src/luminary/luminary-selectors';

/**
 * Recursively collects a map of asset name -> default text from a raw scene
 * design config. The config is an opaque nested structure (asset groups,
 * subslides, etc.), so we walk every object looking for the `{ name, text }`
 * shape rather than assuming a fixed tree layout.
 */
function collectTextByName(node: unknown, map: Map<string, string>): void {
    if (!node || typeof node !== 'object') {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child) => collectTextByName(child, map));
        return;
    }
    const obj = node as Record<string, unknown>;
    if (typeof obj.name === 'string' && typeof obj.text === 'string') {
        map.set(obj.name, obj.text);
    }
    Object.values(obj).forEach((value) => {
        if (value && typeof value === 'object') {
            collectTextByName(value, map);
        }
    });
}

/**
 * Reads the scene-design default text for each named asset by reloading the
 * scene's design config. Returns null if the config can't be read (so callers
 * skip the comparison rather than crash).
 */
function getSceneDesignDefaultText(scene: Scene): Map<string, string> | null {
    try {
        const root = scene.root as unknown as {
            _getSceneDesignConfig?: (sceneDesignId: string, aspectRatio: string) => unknown;
            aspectRatio: string;
        };
        if (typeof root._getSceneDesignConfig !== 'function') {
            return null;
        }
        const config = root._getSceneDesignConfig(scene.sceneDesign, root.aspectRatio);
        const map = new Map<string, string>();
        collectTextByName(config, map);
        return map;
    } catch {
        return null;
    }
}

/**
 * Warning: content still looks like an untouched placeholder. Two heuristics:
 *  - a voice-driven scene group still using the placeholder voiceover /
 *    talking-head base asset, and
 *  - an editable text asset whose text is unchanged from the template's
 *    scene-design default.
 * Both may be intentional, so they are warnings the user should confirm.
 */
export function checkPlaceholderText(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.sceneGroups.forEach((sceneGroup, sceneGroupIndex) => {
        if (isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup)) {
            findings.push({
                check: 'placeholder_text',
                severity: 'warning',
                sceneGroupIndex,
                message:
                    "This voice-driven scene group still uses the placeholder voiceover/talking-head base asset. This may be intentional — confirm you didn't forget to replace it.",
            });
        }
    });

    luminary.scenes.forEach((scene) => {
        const defaults = getSceneDesignDefaultText(scene);
        if (!defaults) {
            return;
        }
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter((asset) => asset.isEditable && asset.text?.trim());
        textAssets.forEach((asset) => {
            const defaultText = defaults.get(asset.name);
            const currentText = asset.text.trim();
            if (
                defaultText !== undefined &&
                defaultText.trim().length > 0 &&
                defaultText.trim() === currentText
            ) {
                findings.push({
                    check: 'placeholder_text',
                    severity: 'warning',
                    sceneId: scene.id,
                    message: `Text "${currentText}" on this scene is unchanged from the template's default. This may be intentional — confirm you didn't forget to clear it.`,
                });
            }
        });
    });

    return findings;
}
